import { isForbiddenAuthz, shouldAttemptRefresh, shouldForceLogout, shouldSkipGlobalAuthHandling } from 'auth/authDecision'
import { forceLogout, getStoredAuth, setStoredAuth } from 'auth/sessionManager'
import axios from 'axios'
import appConfig from 'configs/app.config'
import { REQUEST_HEADER_AUTH_KEY, TOKEN_TYPE } from 'constants/api.constant'
import { AUTH_ERROR_CODES } from 'constants/auth.constant'

const BaseService = axios.create({
    timeout: 600000,
    baseURL: appConfig.apiBaseUrl,
})

const RawService = axios.create({
    timeout: 600000,
    baseURL: appConfig.apiBaseUrl,
})

let refreshPromise = null

const refreshOnce = async () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const { refreshToken } = getStoredAuth()
            if (!refreshToken) {
                throw new Error('Missing refresh token')
            }

            const response = await RawService.post('/api/auth/refresh', { refreshToken })
            const { accessToken, refreshToken: nextRefreshToken, sessionId, status } = response.data || {}

            if (!status || !accessToken || !nextRefreshToken || !sessionId) {
                throw new Error('Invalid refresh payload')
            }

            setStoredAuth({
                accessToken,
                refreshToken: nextRefreshToken,
                sessionId,
            })
        })().finally(() => {
            refreshPromise = null
        })
    }

    return refreshPromise
}

BaseService.interceptors.request.use(config => {
    const { accessToken } = getStoredAuth()

    if (accessToken) {
        config.headers = config.headers || {}
        config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
    }

    return config
}, error => Promise.reject(error))

BaseService.interceptors.response.use(
    response => response,
    async error => {
        const { response, config } = error

        if (!response || !config) {
            return Promise.reject(error)
        }

        const { status, data } = response
        const code = data?.code
        const isRefreshCall = config.url?.includes('/api/auth/refresh')
        const skipAuthRefresh = config.skipAuthRefresh
        const isLoginCall = config.url?.includes('/api/auth/login')

        if (isForbiddenAuthz(status, code)) {
            const forbiddenError = new Error(data?.message || 'Forbidden')
            forbiddenError.code = AUTH_ERROR_CODES.FORBIDDEN
            return Promise.reject(forbiddenError)
        }

        if (status !== 401) {
            return Promise.reject(error)
        }

        if (shouldSkipGlobalAuthHandling({ isLoginCall, isRefreshCall })) {
            return Promise.reject(error)
        }

        if (shouldAttemptRefresh({ status, code, isRetryRequest: config.__isRetryRequest, isRefreshCall, skipAuthRefresh })) {
            config.__isRetryRequest = true
            try {
                await refreshOnce()
                return BaseService(config)
            } catch (refreshError) {
                await forceLogout(code)
                return Promise.reject(refreshError)
            }
        }

        if (shouldForceLogout({ status, code, isRefreshCall, isLoginCall })) {
            await forceLogout(code || 'AUTH_401')
        }

        return Promise.reject(error)
    }
)

export default BaseService